import { resolveAgoraAppId } from "../config/agoraConfig";

/**
 * Validate Agora session fields returned by the support call API.
 */
export function parseSupportAgoraSession(data) {
  const appId = resolveAgoraAppId(typeof data?.appId === "string" ? data.appId : null);
  const channelName = String(data?.channelName ?? data?.channel_name ?? "").trim();
  const token = String(data?.token ?? "").trim();
  const uidRaw = data?.uid;
  const uid = Number(uidRaw);
  const numericUid = Number.isFinite(uid) && uid >= 0 ? uid : 0;

  if (!channelName) {
    throw new Error("Missing Agora channel name from server");
  }
  if (!token || !token.startsWith("007")) {
    throw new Error(
      "Invalid Agora token from server. Restart the API after updating AGORA_APP_ID / AGORA_APP_CERTIFICATE."
    );
  }

  return { appId, channelName, token, uid: numericUid };
}

/**
 * Ensure the browser has microphone permission before starting a call.
 * Triggers the browser prompt if the permission has not been decided yet.
 * @returns {Promise<{ granted: boolean, reason?: "denied" | "no-device" | "unsupported" | "error", error?: unknown }>}
 */
export async function ensureMicrophonePermission() {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    return { granted: false, reason: "unsupported" };
  }

  // Fast path: if the browser already knows the permission is blocked, bail early
  // without prompting (the prompt won't show again once denied).
  try {
    if (navigator.permissions?.query) {
      const status = await navigator.permissions.query({ name: "microphone" });
      if (status.state === "denied") {
        return { granted: false, reason: "denied" };
      }
    }
  } catch {
    // The Permissions API may not support "microphone" in this browser — ignore
    // and fall back to requesting access directly below.
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // Release immediately; Agora opens its own microphone track when joining.
    stream.getTracks().forEach((track) => track.stop());
    return { granted: true };
  } catch (err) {
    const name = err?.name || "";
    if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") {
      return { granted: false, reason: "denied" };
    }
    if (name === "NotFoundError" || name === "DevicesNotFoundError") {
      return { granted: false, reason: "no-device" };
    }
    return { granted: false, reason: "error", error: err };
  }
}

let activeSupportClient = null;
let joinInFlight = null;

function isAgoraConflictError(err) {
  const code = String(err?.code || err?.message || "").toUpperCase();
  return (
    code.includes("UID_CONFLICT") ||
    code.includes("CHANNEL_CONFLICT") ||
    code.includes("ADAPTER_SEND_CONFLICT") ||
    code.includes("INVALID_OPERATION")
  );
}

function attachClient(client, { onRemoteUserJoined, onRemoteUserLeft } = {}) {
  client.on("user-published", async (user, mediaType) => {
    if (mediaType === "audio") {
      await client.subscribe(user, mediaType);
      user.audioTrack?.play();
      onRemoteUserJoined?.(user);
    }
  });

  client.on("user-joined", (user) => {
    onRemoteUserJoined?.(user);
  });

  client.on("user-left", (user) => {
    onRemoteUserLeft?.(user);
  });
}

/**
 * Join a support voice channel as agent (publish mic, play remote audio).
 * @param {{ onRemoteUserJoined?: (user: import("agora-rtc-sdk-ng").IAgoraRTCRemoteUser) => void, onRemoteUserLeft?: (user: import("agora-rtc-sdk-ng").IAgoraRTCRemoteUser) => void }} [options]
 * @returns {{ client: import("agora-rtc-sdk-ng").IAgoraRTCClient, localAudioTrack: import("agora-rtc-sdk-ng").ILocalAudioTrack }}
 */
export async function joinSupportAgoraCall(session, options = {}) {
  if (joinInFlight) {
    await joinInFlight.catch(() => {});
  }

  const run = async () => {
    const { onRemoteUserJoined, onRemoteUserLeft } = options;
    const { appId, channelName, token, uid } =
      typeof session?.token === "string" ? parseSupportAgoraSession(session) : session;

    if (activeSupportClient) {
      await leaveSupportAgoraCall(
        activeSupportClient,
        activeSupportClient.localAudioTrack
      );
    }
    
    if (typeof navigator !== "undefined" && navigator.mediaDevices?.getUserMedia) {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    }
      
    const AgoraRTC = (await import("agora-rtc-sdk-ng")).default;
    const joinOnce = async () => {
      const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
      attachClient(client, { onRemoteUserJoined, onRemoteUserLeft });
      await client.join(appId, channelName, token, uid);
      const localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
      await client.publish([localAudioTrack]);

      for (const user of client.remoteUsers) {
        if (user.hasAudio) {
          await client.subscribe(user, "audio");
          user.audioTrack?.play();
          onRemoteUserJoined?.(user);
        }
      }

      client.localAudioTrack = localAudioTrack;
      activeSupportClient = client;
      return { client, localAudioTrack };
    };

    try {
      return await joinOnce();
    } catch (err) {
      if (!isAgoraConflictError(err)) throw err;
      await leaveSupportAgoraCall(activeSupportClient, activeSupportClient?.localAudioTrack);
      activeSupportClient = null;
      return joinOnce();
    }
  };

  joinInFlight = run();
  try {
    return await joinInFlight;
  } finally {
    joinInFlight = null;
  }
}

export async function leaveSupportAgoraCall(client, localAudioTrack) {
  try {
    localAudioTrack?.close();
    await client?.leave();
  } catch {
    // ignore cleanup errors
  } finally {
    if (client && activeSupportClient === client) {
      activeSupportClient = null;
    }
  }
}

export function formatSupportCallError(error) {
  const code = String(error?.code || "").trim();
  const message = String(error?.response?.data?.message || error?.message || "").trim();
  const combined = `${code} ${message}`.toUpperCase();

  if (combined.includes("ADAPTER_SEND_CONFLICT") || combined.includes("UID_CONFLICT")) {
    return "Could not connect to the call channel. Close any other call tabs and try again.";
  }
  if (combined.includes("CHANNEL_CONFLICT")) {
    return "You are already connected to this call in another window.";
  }
  if (combined.includes("PERMISSION_DENIED") || combined.includes("NOT_ALLOWED")) {
    return "Microphone access is required to start a call.";
  }

  return message || code || "Call failed";
}
